import { useEffect, useRef, useState } from 'react'
import {
  GetManagementActivity, GetManagementOverview, GetManagementStatus
} from '../services/peerToPeer/ManagementService'
import {
  type ManagementActivity, type ManagementBuildingRow, type ManagementDrillLevel,
  type ManagementOverview, type ManagementStatusFilter
} from '../models/peerToPeer/Management'
import { handleApiResponse } from '../utils/apiHelpers'
import useDebounce from './use-debounce'

const SEARCH_DEBOUNCE_MS = 300

interface UseP2PManagementArgs {
  view: 'org' | 'status'
  statusFilter: ManagementStatusFilter
  search: string
  filterLevel: ManagementDrillLevel
  filterOrgId: number | 'all'
}

interface UseP2PManagementType {
  overview: ManagementOverview | null
  yearId: number
  setYearId: (yearId: number) => void
  activity: ManagementActivity | null
  statusRows: ManagementBuildingRow[]
  /** True once the debounced query is non-empty: results come from the flat form table, not the drill-down. */
  searching: boolean
  refresh: () => void
}

export const useP2PManagement = ({ view, statusFilter, search, filterLevel, filterOrgId }: UseP2PManagementArgs): UseP2PManagementType => {
  const [overview, setOverview] = useState<ManagementOverview | null>(null)
  const [yearId, setYearId] = useState<number>(0)
  const [activity, setActivity] = useState<ManagementActivity | null>(null)
  const [statusRows, setStatusRows] = useState<ManagementBuildingRow[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const loadedOverviewYear = useRef<number | null>(null)

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)
  const searching = debouncedSearch.trim() !== ''
  const organizationId = filterOrgId === 'all' ? undefined : filterOrgId

  useEffect(() => {
    if (loadedOverviewYear.current === yearId) return

    const loadOverview = async (): Promise<void> => {
      const result = await handleApiResponse(GetManagementOverview(yearId))
      if (result == null) return
      // Year 0 asks the server for the active year, and the response names the year it actually
      // measured — so the tiles and the id adopted here always describe the same year. Inferring
      // it from yearOptions instead left the tiles showing an empty year-0 rollup.
      const resolvedYear = result.reportingYearId
      loadedOverviewYear.current = resolvedYear
      setOverview(result)
      if (resolvedYear !== yearId) setYearId(resolvedYear)
    }

    void loadOverview()
  }, [yearId])

  // Loaded for every view, not just the Organization one: besides the accordion rows, this
  // response is what the "Filter by" organization dropdown lists, so skipping it while the
  // Status view is open would leave that dropdown offering the previous level's orgs.
  useEffect(() => {
    if (yearId === 0) return
    // Cleanup runs before the next invocation, so this also discards a response that a newer
    // request has already superseded.
    let cancelled = false

    const run = async (): Promise<void> => {
      const result = await handleApiResponse(GetManagementActivity(filterLevel, yearId)) ?? null
      if (!cancelled) setActivity(result)
    }

    void run()
    return () => { cancelled = true }
  }, [yearId, filterLevel, refreshKey])

  // The flat form-results table, shown by the Status view and by a search from either view.
  useEffect(() => {
    if (yearId === 0 || (view !== 'status' && !searching)) return
    let cancelled = false

    const run = async (): Promise<void> => {
      const rows = await handleApiResponse(GetManagementStatus(statusFilter, debouncedSearch, yearId, organizationId)) ?? []
      if (!cancelled) setStatusRows(rows)
    }

    void run()
    return () => { cancelled = true }
  }, [yearId, view, statusFilter, debouncedSearch, searching, organizationId, refreshKey])

  return {
    overview,
    yearId,
    setYearId,
    activity,
    statusRows,
    searching,
    refresh: () => { setRefreshKey(key => key + 1) }
  }
}
